
import { Component, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../../framework/state';
import { getResource, postResource } from '../../../../../framework/client/resource';
import { getEnumerationOptions, OptionValue } from '../../../../../framework/service/options';
import {
    getAccountOptions,
    getCustomerOptions,
    getVendorOptions,
} from '../../../../service/application_options_service';

import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import NumberField from '../../../../../framework/fields/NumberField.vue';
import DateField from '../../../../../framework/fields/DateField.vue';
import PercentageField from '../../../../../framework/fields/PercentageField.vue';
import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import EmailField from '../../../../../framework/fields/EmailField.vue';

import UsageField from '../../../../fields/UsageField.vue';
import MonthsField from '../../../../fields/MonthsField.vue';
import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { VehicleType } from '../../../../../../common/application/model/enums/VehicleType';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { AdminVehicleState, VehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { VehicleIntendedUse } from '../../../../../../common/application/model/enums/VehicleIntendedUse';
import { VehicleUsageType } from '../../../../../../common/application/model/enums/VehicleUsageType';
import { VehicleEnginePowerType } from '../../../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleElectricalClass } from '../../../../../../common/application/model/enums/VehicleElectricalClass';
import { FinancingContractType } from '../../../../../../common/application/model/enums/FinancingContractType';
import { AccountType } from '../../../../../../common/application/model/enums/AccountType';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleMake } from '../../../../../../common/application/model/VehicleMake';
import { VehicleMakeType } from '../../../../../../common/application/model/enums/VehicleMakeType';

import { errorToast, successToast } from '../../../../service/toast_service';
import { updateCachedResourceRow } from '../../../../service/query_cache_service';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';

interface AddVehicleContractLocalState {
    loading: boolean;
    row: VehicleContract;
    customerOptions: OptionValue[];
    vehicleTypeOptions: OptionValue[];
    vehicleStateOptions: OptionValue[];
    vehicleIntendedUseOptions: OptionValue[];
    vehicleUsageTypeOptions: OptionValue[];
    vehicleEnginePowerTypeOptions: OptionValue[];
    vehicleElectricalClassOptions: OptionValue[];
    financingContractTypeOptions: OptionValue[];
    smartFleetContractTypeOptions: OptionValue[];
    customerExpenseAccountOptions: OptionValue[];
    vendorOptions: OptionValue[];
    showAllVehicleFields: boolean;
    showAllSmartFleetFields: boolean;
    showAllFinancingFields: boolean;
    vehicleMakeId: string[];
}

@Component({
    components: {
        MonthsField,
        UsageField,
        PercentageField,
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        ValidationObserver,
        EmailField,
        AutoCompleteMultipleField,
    },
})
export default class AddVehicleContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    // Properties

    // State
    shared = sharedState;
    local: AddVehicleContractLocalState = {
        loading: true,
        row: {
            vehicleState: AdminVehicleState.DRAFT,
        } as VehicleContract,
        customerOptions: [],
        vehicleTypeOptions: [],
        vehicleStateOptions: [],
        vehicleIntendedUseOptions: [],
        vehicleUsageTypeOptions: [],
        vehicleEnginePowerTypeOptions: [],
        vehicleElectricalClassOptions: [],
        financingContractTypeOptions: [],
        smartFleetContractTypeOptions: [],
        customerExpenseAccountOptions: [],
        vendorOptions: [],
        showAllVehicleFields: false,
        showAllSmartFleetFields: false,
        showAllFinancingFields: false,
        vehicleMakeId: [],
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        this.local.customerOptions = await getCustomerOptions();

        this.local.vehicleTypeOptions = getEnumerationOptions('VehicleType', Object.keys(VehicleType), this);
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.vehicleIntendedUseOptions = getEnumerationOptions(
            'VehicleIntendedUse',
            Object.keys(VehicleIntendedUse),
            this,
        );
        this.local.vehicleUsageTypeOptions = getEnumerationOptions(
            'VehicleUsageType',
            Object.keys(VehicleUsageType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );

        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );

        if (this.local.row.customerId) {
            this.local.customerExpenseAccountOptions = await getAccountOptions(
                this.local.row.customerId,
                AccountType.EXPENSE,
            );
        }

        this.local.vendorOptions = await getVendorOptions();
        this.local.loading = false;
    }

    @Watch('local.row.customerId')
    async customerIdChange() {
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('local.row.smartFleetContractStart')
    smartFleetContractStartChange(smartFleetContractStart: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!smartFleetContractStart || !this.local.row.smartFleetContractEnd) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractPeriodMonths: this.calculateContractPeriod(
                smartFleetContractStart,
                this.local.row.smartFleetContractEnd as any,
            ),
        };
    }

    @Watch('local.row.smartFleetContractEnd')
    smartFleetContractEndChange(smartFleetContractEnd: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!smartFleetContractEnd || !this.local.row.smartFleetContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractPeriodMonths: this.calculateContractPeriod(
                this.local.row.smartFleetContractStart as any,
                smartFleetContractEnd,
            ),
        };
    }

    @Watch('local.row.smartFleetContractPeriodMonths')
    smartFleetContractPeriodMonthsChange(smartFleetContractPeriodMonths: number | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || focusedElement.name !== 'smartFleetContractPeriodMonths') {
            return;
        }

        if (!smartFleetContractPeriodMonths || !this.local.row.smartFleetContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractEnd: this.calculateEndDate(
                this.local.row.smartFleetContractStart as any,
                smartFleetContractPeriodMonths as number,
            ),
        };
    }

    @Watch('local.row.financingContractStart')
    financingContractStartChange(financingContractStart: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!financingContractStart || !this.local.row.financingContractEnd) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractPeriodMonths: this.calculateContractPeriod(
                financingContractStart,
                this.local.row.financingContractEnd as any,
            ),
        };
    }

    @Watch('local.row.financingContractEnd')
    financingContractEndChange(financingContractEnd: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!financingContractEnd || !this.local.row.financingContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractPeriodMonths: this.calculateContractPeriod(
                this.local.row.financingContractStart as any,
                financingContractEnd,
            ),
        };
    }

    @Watch('local.row.financingContractPeriodMonths')
    financingContractPeriodMonthsChange(financingContractPeriodMonths: number | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || focusedElement.name !== 'financingContractPeriodMonths') {
            return;
        }

        if (!financingContractPeriodMonths || !this.local.row.financingContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractEnd: this.calculateEndDate(
                this.local.row.financingContractStart as any,
                financingContractPeriodMonths as number,
            ),
        };
    }

    dropdownClosed(vehicleMakeIds: string[], idField: string): void {
        if (!vehicleMakeIds[0]) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            vehicleMakeId: vehicleMakeIds[0],
        };
    }

    get vehicleMakeId(): string[] {
        return this.local.row.vehicleMakeId ? [this.local.row.vehicleMakeId] : [];
    }

    async addVehicleMake(name: string) {
        try {
            const newVehicleMake: VehicleMake = {
                id: '',
                created: new Date(),
                modified: new Date(),
                name,
                description: '',
                type: VehicleMakeType.CAR,
            };

            const vehicleMake = await postResource<VehicleMake>(ApplicationResource.VEHICLE_MAKE, newVehicleMake);

            setTimeout(() => (this.local.vehicleMakeId = [vehicleMake.id]), 1000);
        } catch (e) {
            errorToast(this, 'error.couldNotInsertVehicleMake');
        }
    }

    copyDateValues(source: string) {
        if (source === 'smartfleet') {
            this.local.row = {
                ...this.local.row,
                financingContractStart: this.local.row.smartFleetContractStart,
                financingContractEnd: this.local.row.smartFleetContractEnd,
                financingContractPeriodMonths: this.local.row.smartFleetContractPeriodMonths,
            };
        } else {
            this.local.row = {
                ...this.local.row,
                smartFleetContractStart: this.local.row.financingContractStart,
                smartFleetContractEnd: this.local.row.financingContractEnd,
                smartFleetContractPeriodMonths: this.local.row.financingContractPeriodMonths,
            };
        }
    }

    add() {
        this.addResource(false);
    }

    addAndClose() {
        this.addResource(true);
    }

    private calculateContractPeriod(start: string, end: string): number {
        return Math.round(
            DateTime.fromFormat(end, 'yyyy-MM-dd').diff(DateTime.fromFormat(start, 'yyyy-MM-dd'), 'months').months,
        );
    }

    private calculateEndDate(start: string, duration: number): Date {
        return dateToDbDateString(
            DateTime.fromFormat(start, 'yyyy-MM-dd', { zone: 'UTC' }).plus({ months: duration }).toJSDate(),
        ) as any;
    }

    private isActiveDatePicker(classList: DOMTokenList): boolean {
        return classList.value === 'datepicker-cell is-selectable';
    }

    private async addResource(goBack: boolean) {
        try {
            if (!this.local.row.smartFleetContractHandoverDate) {
                this.local.row = {
                    ...this.local.row,
                    smartFleetContractHandoverDate: this.local.row.smartFleetContractStart,
                };
            }

            const vehicleMake = (await getResource<VehicleMake>(
                ApplicationResource.VEHICLE_MAKE,
                this.local.row.vehicleMakeId,
            ))!!;
            const newVehicleContract = await postResource<VehicleContract>(this.resourceType, {
                ...this.local.row,
                vehicleMake: vehicleMake.name,
            });
            successToast(this, 'message.vehicleContractSaved');
            updateCachedResourceRow<VehicleContract>(newVehicleContract, newVehicleContract.id, this.resourceType);

            if (goBack) {
                this.back();
            } else {
                await this.$router.replace(`/${this.resourceType}/${newVehicleContract.id}/edit`);
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }
}
